/**
 * useSpyIntelligence — Custom hook for all Spy/Intelligence functionality.
 *
 * Manages: spy settings (Firebase), single spy analysis, mass spy queue,
 * intelligence data application (emails, socials, phones, descriptions),
 * and the spy data preview modal state.
 *
 * @param {Object} deps
 * @param {string} deps.userId - Current user ID
 * @param {Function} deps.handleEdit - Parent's inline edit handler (rowId, field, value)
 * @param {Array} deps.displayRows - Filtered/sorted rows currently visible
 * @param {Function} deps.saveScrollPosition - Saves current scroll position
 * @param {Function} deps.restoreScrollPosition - Restores saved scroll position
 * @param {Function} deps.fetchData - Parent's data reload function
 *
 * @returns {Object} spy — Destructure: { spyLoadingRows, spyDataPreview, spySettings,
 *   handleSpyClick, handleSpyPreviewClose, handleSpyPreviewApply,
 *   handleSpyPreviewSelectiveApply, isMassSpyActive, massSpyQueue, massSpyProgress,
 *   massSpyTotal, massSpyResults, massSpyContacts, massSpyProcessedData,
 *   showMassSpyReview, handleStartMassSpy, closeMassSpy,
 *   handleMassSpyReviewConfirm, handleMassSpyReviewCancel }
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { ref, onValue } from "firebase/database";
import { httpsCallable } from "firebase/functions";
import { doc, deleteDoc } from "firebase/firestore";
import { database, firestore, functions } from "../firebaseConfig";
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../utils/cloudFunctions';
import { computeAndStoreSignals } from './leadSignalEngine';
import { calculateSpyCost } from '../config/creditPlans';
import { useResearchLicenseRef } from '../hooks/useResearchAccess';
import { computeHasEmail, isPrivacyEmail } from '../shared/enums';
import {
  lookupBlacklistForEmails,
  filterBlacklistedEmails,
  stripBlacklistedEmailsFromDms,
} from '../shared/blacklistUtils';
import { dedupFieldPatch } from '../shared/contactIdentity';
import { addressUpdatesForContact } from '../shared/researchLocation';
import { recordCompanyRecord } from '../shared/recordIntelligenceLog';

/**
 * Extracts every candidate email from one or more `intelligenceData`
 * objects so we can run a single scalable blacklist lookup
 * (`lookupBlacklistForEmails`) instead of reading the whole blacklist
 * collection. Cost goes from O(blacklist size) → O(unique emails + domains).
 *
 * Covers all fields where Web Research can surface NEW emails on contacts
 * that didn't have one at import time:
 *   - `emailsFoundInSearch`
 *   - `openAIAnalysis.emails`
 *   - `aiContentAnalysis.contactSummary.emails`
 *   - `decisionMakers[].email`
 */
function collectCandidateEmails(intelligenceDataList) {
  const list = Array.isArray(intelligenceDataList) ? intelligenceDataList : [intelligenceDataList];
  const out = [];
  for (const intel of list) {
    if (!intel) continue;
    if (Array.isArray(intel.emailsFoundInSearch)) {
      for (const e of intel.emailsFoundInSearch) if (typeof e === 'string') out.push(e);
    }
    const oaEmails = intel.openAIAnalysis?.emails;
    if (Array.isArray(oaEmails)) {
      for (const e of oaEmails) if (typeof e === 'string') out.push(e);
    }
    const cs = intel.aiContentAnalysis?.contactSummary?.emails;
    if (Array.isArray(cs)) {
      for (const item of cs) {
        if (typeof item === 'string') out.push(item);
        else if (item?.email && typeof item.email === 'string') out.push(item.email);
      }
    }
    if (Array.isArray(intel.decisionMakers)) {
      for (const dm of intel.decisionMakers) {
        if (dm?.email && typeof dm.email === 'string') out.push(dm.email);
      }
    }
  }
  return out;
}

/**
 * Extracts structured research data from raw intelligence results.
 * Stored on the contact doc for Pass R signal extraction and future modules.
 */
// Normalize the categorized research topics (keyFacts / buySignals / latestNews)
// from the raw Grok enrichment into clean, UI-ready arrays. Kept tolerant so old
// or partial payloads never crash. Shared shape mirrored in
// functions/processOutreachJobs.js — keep both in sync.
function normalizeResearchCategories(grok = {}, lp = {}) {
  const qsRaw = grok.quickStats && typeof grok.quickStats === 'object' ? grok.quickStats : {};
  const cleanStat = (v) => {
    const flat = v && typeof v === 'object' ? Object.values(v).filter((x) => x != null && x !== '').join(', ') : v;
    const s = flat == null ? '' : String(flat).trim();
    return s && s.toLowerCase() !== 'null' && s.toLowerCase() !== 'unknown' ? s : null;
  };
  const quickStats = {
    employees: cleanStat(qsRaw.employees) || cleanStat(lp.companySizeEstimate),
    revenue: cleanStat(qsRaw.revenue),
    founded: cleanStat(qsRaw.founded),
    location: cleanStat(qsRaw.location),
  };
  const hasQuickStats = Object.values(quickStats).some(Boolean);

  const keyFacts = Array.isArray(grok.keyFacts)
    ? grok.keyFacts.map(f => (typeof f === 'string' ? f.trim() : '')).filter(Boolean).slice(0, 8)
    : null;

  const buySignals = Array.isArray(grok.buySignals)
    ? grok.buySignals
        .map(s =>
          typeof s === 'string'
            ? { signal: s.trim(), evidence: null }
            : s && s.signal
              ? {
                  signal: String(s.signal).trim(),
                  evidence: s.evidence ? String(s.evidence).trim() : null,
                  sourceUrl: /^https?:\/\//i.test(s.sourceUrl || '') ? String(s.sourceUrl).trim() : null,
                }
              : null
        )
        .filter(Boolean)
        .slice(0, 6)
    : (lp.budgetIndicators ? [{ signal: String(lp.budgetIndicators).trim(), evidence: null }] : null);

  const latestNews = Array.isArray(grok.currentNews)
    ? grok.currentNews
        .map(n =>
          n && n.headline
            ? {
                headline: String(n.headline).trim(),
                date: n.date ? String(n.date).trim() : null,
                hookValue: n.hookValue ? String(n.hookValue).trim() : null,
                sourceUrl: /^https?:\/\//i.test(n.sourceUrl || '') ? String(n.sourceUrl).trim() : null,
              }
            : null
        )
        .filter(Boolean)
        .slice(0, 5)
    : null;

  return {
    quickStats: hasQuickStats ? quickStats : null,
    keyFacts: keyFacts && keyFacts.length ? keyFacts : null,
    buySignals: buySignals && buySignals.length ? buySignals : null,
    latestNews: latestNews && latestNews.length ? latestNews : null,
  };
}

function normalizeShopIntelligence(shop) {
  if (!shop || !shop.isShop) return null;
  const arr = (v) => (Array.isArray(v) ? v.map((x) => String(x).trim()).filter(Boolean).slice(0, 5) : []);
  const trust =
    shop.trustScore && shop.trustScore.score
      ? { source: shop.trustScore.source ? String(shop.trustScore.source).trim() : null, score: String(shop.trustScore.score).trim() }
      : null;
  return {
    platform: shop.platform ? String(shop.platform).trim() : null,
    productCount: shop.productCountRange || shop.productCountEstimate || null,
    categories: arr(shop.mainCategories),
    brands: arr(shop.notableBrands),
    founded: shop.foundedYear || (shop.shopAgeYears ? `${shop.shopAgeYears} yrs` : null),
    trustScore: trust,
    summary: shop.summary ? String(shop.summary).trim() : null,
  };
}

function normalizeLinkedinCompany(li) {
  const cp = li && li.companyProfile ? li.companyProfile : null;
  if (!cp || !cp.url) return null;
  return {
    url: String(cp.url).trim(),
    name: cp.name ? String(cp.name).trim() : null,
    description: cp.description ? String(cp.description).trim() : null,
    employeesFound: Array.isArray(li.employees) ? li.employees.length : 0,
    confidence: cp.confidence != null ? cp.confidence : null,
  };
}

// The frontend replaces researchIntelligence wholesale, so a failed crawl has
// to carry the previous facts/signals over explicitly.
function websiteResearchFields(intelligenceData, existingRI) {
  const prev = existingRI && typeof existingRI === 'object' ? existingRI : {};
  const crawl = intelligenceData.websiteCrawl;
  const crawlOk = !!(crawl && crawl.ok);
  const fresh = Array.isArray(intelligenceData.newestSignals) && (intelligenceData.newestSignals.length > 0 || crawlOk);
  const out = {
    websiteFacts: crawlOk ? crawl : (prev.websiteFacts || null),
    newestSignals: fresh ? intelligenceData.newestSignals : (prev.newestSignals || null),
    newestSignalsAt: fresh ? new Date().toISOString() : (prev.newestSignalsAt || null),
  };
  return out;
}

function buildResearchIntelligence(intelligenceData, existingRI = null) {
  const dms = intelligenceData.decisionMakers || [];
  const lp = intelligenceData.leadPotential || intelligenceData.openAIAnalysis?.leadPotential || {};
  const grok = intelligenceData.grokEnrichment || {};
  const categories = normalizeResearchCategories(grok, lp);
  const shopIntelligence = normalizeShopIntelligence(intelligenceData.shopIntelligence);
  const linkedinCompany = normalizeLinkedinCompany(intelligenceData.linkedinIntelligence);

  const newRI = {
    modules: intelligenceData.modulesUsed || ['web_intelligence'],
    companySizeEstimate: lp.companySizeEstimate || null,
    industryFit: lp.industryFit || null,
    leadScore: lp.score || null,
    leadRating: lp.rating || null,
    onlinePresenceScore: lp.onlinePresenceScore || null,
    budgetIndicators: lp.budgetIndicators || null,
    reasoning: lp.reasoning || null,
    companyInfo: typeof grok.companyInfo === 'string' ? grok.companyInfo : null,
    // Categorized research topics (v2)
    quickStats: categories.quickStats,
    keyFacts: categories.keyFacts,
    buySignals: categories.buySignals,
    latestNews: categories.latestNews,
    shopIntelligence,
    linkedinCompany,
    decisionMakerCount: dms.length,
    decisionMakerRoles: dms.map(dm => dm.role).filter(Boolean),
    linkedinCompanyProfile: intelligenceData.linkedinIntelligence?.companyProfile?.url || null,
    linkedinEmployeesFound: intelligenceData.linkedinIntelligence?.employees?.length || 0,
    confidenceScore: intelligenceData.confidenceScore || 0,
    researchedAt: new Date().toISOString(),
    version: 2,
    ...websiteResearchFields(intelligenceData, existingRI),
  };

  if (existingRI) {
    if (existingRI.instagramInsight) newRI.instagramInsight = existingRI.instagramInsight;
    if (existingRI.instagramPosts) newRI.instagramPosts = existingRI.instagramPosts;
    if (existingRI.instagramMeta) newRI.instagramMeta = existingRI.instagramMeta;
  }

  return newRI;
}

const DEFAULT_SPY_SETTINGS = {
  activeModules: ['core', 'email', 'phone', 'social', 'description'],
  advancedSettings: {
    autoApplyHighConfidence: true,
    confidenceThreshold: 50,
    preserveExistingData: true,
    targetCountry: 'auto'
  },
  customContext: ''
};

function buildIntelligencePayload(rowData, userId, spySettings) {
  return {
    businessName: rowData.business_name || '',
    existingDescription: rowData.lead_description || '',
    lead_website: rowData.lead_website || '',
    lead_email: rowData.lead_email || '',
    existingSocial: {
      linkedin: rowData.lead_linkedin || '',
      instagram: rowData.lead_instagram || rowData.lead_instagram_username || '',
      facebook: rowData.lead_facebook || '',
      tiktok: rowData.lead_tiktok || ''
    },
    lead_city: rowData.lead_city || '',
    lead_address: rowData.lead_address || '',
    lead_country: rowData.lead_country || '',
    lead_telephone: rowData.lead_telephone || '',
    rowId: rowData.id,
    userId,
    modules: spySettings.activeModules,
    customContext: spySettings.customContext || '',
    targetCountry: spySettings.advancedSettings?.targetCountry || 'auto'
  };
}

function selectBestSocialUrl(platform, urls, businessName) {
  if (!urls || urls.length === 0) return null;
  if (urls.length === 1) return urls[0];

  const businessNameLower = (businessName || '').toLowerCase().replace(/\s+/g, '');

  const urlScores = urls.map(url => {
    let score = 0;
    const urlLower = url.toLowerCase();

    const usernameMatch = urlLower.includes(businessNameLower) ||
      businessNameLower.includes(urlLower.split('/').pop()?.replace(/[^a-z0-9]/g, '') || '');
    if (usernameMatch) score += 50;

    if (url.length < 50) score += 20;
    else if (url.length < 100) score += 10;

    if (urlLower.includes(`${platform}.com/`) && !urlLower.includes('www.')) score += 15;

    const hasProfile = /\/(profile|user|@|page)\//.test(urlLower) || urlLower.split('/').length <= 4;
    if (hasProfile) score += 25;

    if (!urlLower.includes('?') && !urlLower.includes('#')) score += 10;

    if (platform === 'facebook') {
      if (urlLower.includes('/pages/')) score -= 10;
      if (urlLower.includes('/profile.php?')) score -= 15;
    }

    return { url, score };
  });

  urlScores.sort((a, b) => b.score - a.score);
  return urlScores[0].url;
}

// Generic / shared mailbox local-parts (info@, kontakt@, …). Mirrors the
// backend GENERIC_LOCAL_PREFIXES so Research and the DM Email Finder classify
// addresses identically. These are COMPANY addresses, never personal — so they
// must never be attached to a decision maker.
const GENERIC_EMAIL_LOCALPARTS = new Set([
  'info', 'mail', 'kontakt', 'contact', 'office', 'team', 'sales', 'support',
  'hello', 'hi', 'hallo', 'moin', 'noreply', 'no-reply', 'newsletter', 'post',
  'presse', 'press', 'media', 'billing', 'jobs', 'careers', 'career', 'hr',
  'datenschutz', 'privacy', 'webmaster', 'hostmaster', 'postmaster', 'abuse',
  'service', 'zentrale', 'buchhaltung', 'vertrieb', 'verkauf', 'einkauf',
  'purchasing', 'empfang', 'reception', 'kundenservice', 'bestellung', 'order',
  'anfrage', 'welcome', 'shop', 'web', 'admin',
]);

/** True if the address is a generic/role mailbox (not a personal address). */
function isGenericEmail(email) {
  if (!email || typeof email !== 'string' || !email.includes('@')) return false;
  const local = email.toLowerCase().trim().split('@')[0].split('+')[0];
  return GENERIC_EMAIL_LOCALPARTS.has(local);
}

/** Collect generic mailboxes that Research attached to a decision maker. */
function collectGenericDmEmailsFromList(decisionMakers) {
  if (!Array.isArray(decisionMakers)) return [];
  const out = [];
  for (const dm of decisionMakers) {
    if (dm?.email && typeof dm.email === 'string' && isGenericEmail(dm.email) && !isPrivacyEmail(dm.email)) {
      out.push(dm.email.trim());
    }
  }
  return out;
}

function extractCleanEmails(intelligenceData) {
  const emailSources = [];

  if (intelligenceData.emailsFoundInSearch && Array.isArray(intelligenceData.emailsFoundInSearch)) {
    emailSources.push(...intelligenceData.emailsFoundInSearch);
  }
  if (intelligenceData.openAIAnalysis?.emails && Array.isArray(intelligenceData.openAIAnalysis.emails)) {
    emailSources.push(...intelligenceData.openAIAnalysis.emails);
  }
  if (intelligenceData.aiContentAnalysis?.contactSummary?.emails && Array.isArray(intelligenceData.aiContentAnalysis.contactSummary.emails)) {
    intelligenceData.aiContentAnalysis.contactSummary.emails.forEach(item => {
      if (typeof item === 'string') emailSources.push(item);
      else if (item?.email && typeof item.email === 'string') emailSources.push(item.email);
    });
  }

  const corruptPatterns = [
    /u00[0-9a-fA-F]{2}/,
    /[^a-zA-Z0-9@._-]/,
    /^[^a-zA-Z]/,
    /sterreich/,
    /österreich/i
  ];

  return [...new Set(emailSources)].filter(email => {
    if (!email || typeof email !== 'string') return false;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return false;
    // Privacy / data-protection mailboxes (privacy@, datenschutz@, dsgvo@ …) are
    // legally mandated GDPR inboxes, never an outreach contact — never add them.
    if (isPrivacyEmail(email)) return false;
    return !corruptPatterns.some(p => p.test(email));
  });
}

function extractPhones(intelligenceData) {
  const sources = [];
  if (intelligenceData.phoneNumber && typeof intelligenceData.phoneNumber === 'string') sources.push(intelligenceData.phoneNumber);
  if (intelligenceData.openAIAnalysis?.phone && typeof intelligenceData.openAIAnalysis.phone === 'string') sources.push(intelligenceData.openAIAnalysis.phone);
  if (intelligenceData.aiContentAnalysis?.contactSummary?.phones && Array.isArray(intelligenceData.aiContentAnalysis.contactSummary.phones)) {
    intelligenceData.aiContentAnalysis.contactSummary.phones.forEach(item => {
      if (typeof item === 'string') sources.push(item);
      else if (item?.phone && typeof item.phone === 'string') sources.push(item.phone);
    });
  }
  if (intelligenceData.scrapingResults?.data?.phones && Array.isArray(intelligenceData.scrapingResults.data.phones)) {
    sources.push(...intelligenceData.scrapingResults.data.phones.filter(p => typeof p === 'string'));
  }
  return [...new Set(sources.filter(Boolean))].filter(p => p.trim() !== '');
}

function extractEnhancedDescription(intelligenceData, contactData) {
  if (intelligenceData.description) return intelligenceData.description;
  if (intelligenceData.openAIAnalysis?.description) return intelligenceData.openAIAnalysis.description;

  if (intelligenceData.aiContentAnalysis?.detailedSummary) {
    let desc = intelligenceData.aiContentAnalysis.detailedSummary;
    if (intelligenceData.aiContentAnalysis.keyHighlights?.length > 0) {
      desc += '\n\n🎯 KEY HIGHLIGHTS:\n';
      intelligenceData.aiContentAnalysis.keyHighlights.forEach(h => { desc += `• ${h}\n`; });
    }
    if (intelligenceData.aiContentAnalysis.contactSummary?.businessHours) {
      desc += `\n🕒 BUSINESS HOURS: ${intelligenceData.aiContentAnalysis.contactSummary.businessHours}`;
    }
    return desc;
  }

  if (intelligenceData.aiAnalysis?.businessSummary) {
    let desc = intelligenceData.aiAnalysis.businessSummary;
    if (intelligenceData.aiAnalysis.emailsFound?.length > 0) {
      desc += `\n\n📧 CONTACT VERIFIED: ${intelligenceData.aiAnalysis.emailsFound.length} professional email address${intelligenceData.aiAnalysis.emailsFound.length > 1 ? 'es' : ''} found`;
    }
    const socialCount = Object.values(intelligenceData.aiAnalysis.socialMediaLinks || {}).filter(Boolean).length;
    if (socialCount > 0) {
      desc += `\n🌐 DIGITAL PRESENCE: ${socialCount} verified social media profile${socialCount > 1 ? 's' : ''}`;
    }
    return desc;
  }

  if (intelligenceData.confidenceScore > 70 || intelligenceData.aiAnalysis?.overallConfidence > 70) {
    const name = contactData.business_name || 'Business';
    const conf = intelligenceData.confidenceScore || intelligenceData.aiAnalysis?.overallConfidence || 0;
    let desc = `Professional business profile for ${name}. Enhanced intelligence analysis completed with ${conf}% confidence.`;
    if (intelligenceData.emailsFoundInSearch?.length > 0 || intelligenceData.aiAnalysis?.emailsFound?.length > 0) {
      desc += ` Contact information verified and available.`;
    }
    return desc;
  }

  return null;
}

function buildSpyApplyUpdates(intelligenceData, contactData, blacklistSets = null) {
  const updates = [];
  // Collected for caller — every email that the research surfaced AND
  // that was on the blacklist. Used to offer one-click contact removal.
  const blacklistedEmails = [];

  // 1. Emails — Web Research can discover NEW emails on contacts that had
  // none at import time. Those new addresses bypass the import-time
  // blacklist check, so we filter them again here.
  let cleanEmails = extractCleanEmails(intelligenceData);
  // Research sometimes attaches a generic mailbox (info@, kontakt@ …) to a
  // decision maker. Those are COMPANY addresses, not personal — route them into
  // the contact's general email field instead (they get stripped from the DM
  // below). Merge with the existing lead_email so we never clobber a real one.
  const genericDmEmails = collectGenericDmEmailsFromList(intelligenceData.decisionMakers);
  if (genericDmEmails.length > 0) {
    const existing = String(contactData?.lead_email || '')
      .split(/[,;\s]+/)
      .map(e => e.trim())
      .filter(Boolean);
    cleanEmails = [...new Set([...existing, ...cleanEmails, ...genericDmEmails])];
  }
  if (blacklistSets) {
    const allowed = [];
    for (const e of cleanEmails) {
      if (filterBlacklistedEmails([e], blacklistSets).length === 0) blacklistedEmails.push(e);
      else allowed.push(e);
    }
    if (blacklistedEmails.length > 0) {
      console.log(`[SPY-APPLY] Skipped ${blacklistedEmails.length} blacklisted email${blacklistedEmails.length > 1 ? 's' : ''} for ${contactData?.business_name || contactData?.id}`);
    }
    cleanEmails = allowed;
  }
  if (cleanEmails.length > 0) {
    updates.push({ field: 'lead_email', value: cleanEmails.join(', ') });
  }

  // 2. Social Media
  const socialSources = [
    intelligenceData.socialProfiles || {},
    intelligenceData.aiAnalysis?.socialMediaLinks || {},
    intelligenceData.aiContentAnalysis?.socialMediaProfiles || {},
    intelligenceData.scrapingResults?.data?.socialLinks || {}
  ];

  const platforms = ['instagram', 'facebook', 'linkedin', 'tiktok', 'twitter', 'youtube'];
  const platformMapping = {
    instagram: 'lead_instagram', facebook: 'lead_facebook',
    linkedin: 'lead_linkedin', tiktok: 'lead_tiktok',
    twitter: 'lead_twitter', youtube: 'lead_youtube'
  };

  // LinkedIn Intelligence company profile takes priority (Serper-based, higher accuracy)
  const liCompanyUrl = intelligenceData.linkedinIntelligence?.companyProfile?.url;

  const PLATFORM_DOMAINS = {
    instagram: 'instagram.com', facebook: 'facebook.com', linkedin: 'linkedin.com',
    tiktok: 'tiktok.com', twitter: 'x.com', youtube: 'youtube.com'
  };
  const normalizeSocialUrl = (val, plat) => {
    if (!val || typeof val !== 'string' || val === 'null') return null;
    const v = val.trim();
    if (!v) return null;
    if (v.startsWith('http')) return v;
    const domain = PLATFORM_DOMAINS[plat];
    if (domain && v.includes(domain)) return `https://${v}`;
    if (domain) return `https://${domain}/${v.replace('@', '')}`;
    return null;
  };

  platforms.forEach(platform => {
    const urls = socialSources
      .map(s => normalizeSocialUrl(s[platform], platform))
      .filter(Boolean)
      .filter((url, i, arr) => arr.indexOf(url) === i);

    if (platform === 'linkedin' && liCompanyUrl) {
      updates.push({ field: 'lead_linkedin', value: liCompanyUrl });
    } else if (urls.length > 0) {
      const best = urls.length === 1 ? urls[0] : selectBestSocialUrl(platform, urls, contactData.business_name);
      updates.push({ field: platformMapping[platform] || `lead_${platform}`, value: best });
    }
  });

  // 2b. Decision Makers (crawler already merges all sources — use as-is)
  // Blacklist guard: strip emails from DMs whose address is on the
  // blacklist. The DM record itself stays so the contact still has the
  // person + role + socials, but the email is wiped to prevent it from
  // being used for outreach later.
  let allDecisionMakers = intelligenceData.decisionMakers || [];
  // Strip generic/company mailboxes from DMs — they were routed to lead_email
  // above. A decision maker keeps only a personal address (or none at all);
  // an info@/kontakt@ is never assigned to a person.
  if (allDecisionMakers.length > 0) {
    allDecisionMakers = allDecisionMakers.map(dm => (
      dm?.email && isGenericEmail(dm.email) ? { ...dm, email: null } : dm
    ));
  }
  if (blacklistSets && allDecisionMakers.length > 0) {
    // Collect blocked DM emails before stripping for caller feedback.
    for (const dm of allDecisionMakers) {
      if (dm?.email && filterBlacklistedEmails([dm.email], blacklistSets).length === 0) {
        blacklistedEmails.push(dm.email);
      }
    }
    const { dms, removedCount } = stripBlacklistedEmailsFromDms(allDecisionMakers, blacklistSets);
    allDecisionMakers = dms;
    if (removedCount > 0) {
      console.log(`[SPY-APPLY] Wiped ${removedCount} blacklisted DM email${removedCount > 1 ? 's' : ''} for ${contactData?.business_name || contactData?.id}`);
    }
  }
  if (allDecisionMakers.length > 0) {
    updates.push({ field: 'lead_decision_makers', value: JSON.stringify(allDecisionMakers) });
  }

  // 2c. Lead Potential
  if (intelligenceData.leadPotential && typeof intelligenceData.leadPotential === 'object') {
    const lp = intelligenceData.leadPotential;
    if (lp.score !== undefined && lp.score !== null) {
      updates.push({ field: 'lead_score', value: String(lp.score) });
    }
    updates.push({ field: 'lead_potential_data', value: JSON.stringify(lp) });
  }

  // 3. Phone (only if no existing phone)
  const existingPhone = contactData.lead_telephone;
  const hasExisting = existingPhone && typeof existingPhone === 'string' && existingPhone.trim() !== '' && existingPhone !== '-' && existingPhone !== 'null';
  if (!hasExisting) {
    const phones = extractPhones(intelligenceData);
    if (phones.length > 0) updates.push({ field: 'lead_telephone', value: phones[0] });
  }

  // 4. Website
  const websiteFromCrawler = intelligenceData.website;
  const currentWebsite = contactData.lead_website;
  const isValid = currentWebsite && typeof currentWebsite === 'string' && currentWebsite.trim() !== '' && currentWebsite !== '-' && currentWebsite !== 'none' && currentWebsite !== 'null' && currentWebsite.includes('.');

  if (websiteFromCrawler && (!isValid || websiteFromCrawler !== currentWebsite)) {
    updates.push({ field: 'lead_website', value: websiteFromCrawler });
  }

  // 5. Address, postcode and city — only columns that are still empty.
  const addressPatch = addressUpdatesForContact(intelligenceData, contactData);
  for (const [field, value] of Object.entries(addressPatch)) {
    updates.push({ field, value });
  }

  // 6. Description
  const desc = extractEnhancedDescription(intelligenceData, contactData);
  if (desc && desc.trim() !== '') updates.push({ field: 'lead_description', value: desc });

  // Keep the denormalized has_email index in sync with any newly discovered
  // lead_email / decision-maker emails so outreach auto-pools and flow
  // `has_email` conditions recognize DM-only contacts.
  const mergedForHasEmail = { ...contactData };
  for (const u of updates) {
    if (u.field === 'lead_email' || u.field === 'lead_decision_makers') mergedForHasEmail[u.field] = u.value;
  }
  if (computeHasEmail(mergedForHasEmail)) {
    updates.push({ field: 'has_email', value: true });
  }

  // Phase B (additiv, 2026-08): Dedup-Index-Felder synchronisieren, wenn
  // Research eine der drei identitätsrelevanten Spalten anfasst. Verhindert
  // Blind Spots, wenn Research eine neue E-Mail/Website an einen Kontakt
  // anhängt.
  // - Fail-safe: bei Fehler bleibt der Original-Payload unverändert.
  // - Idempotent: dedupFieldPatch liefert nur real geänderte Felder zurück.
  try {
    const identityPatch = {};
    for (const u of updates) {
      if (u.field === 'lead_email' || u.field === 'lead_website' || u.field === 'business_name') {
        identityPatch[u.field] = u.value;
      }
    }
    if (Object.keys(identityPatch).length > 0) {
      const dedup = dedupFieldPatch(contactData || {}, identityPatch);
      for (const [field, value] of Object.entries(dedup)) {
        updates.push({ field, value });
      }
    }
  } catch (err) {
    console.warn('[dedup-sync] research-apply patch skipped (non-blocking):', err && err.message);
  }

  return { updates, blacklistedEmails: [...new Set(blacklistedEmails)] };
}

/**
 * Resolve which of the emails surfaced by Web Research are on the user's
 * blacklist, returned as a lowercased Set for cheap O(1) lookups in the
 * preview UI. Domain-level blocks are expanded to all matching addresses
 * from the candidate list so the UI can mark them too without needing the
 * domain-blacklist set.
 *
 * Used by the preview opener so the modal can render blacklisted emails
 * with a strike-through + badge BEFORE the user clicks Apply (instead of
 * silently filtering them on apply and only surfacing the blocked-contact
 * warning after the fact).
 */
async function computeBlacklistedEmailsSet(intelligenceData, userId) {
  try {
    const candidates = collectCandidateEmails(intelligenceData);
    if (!candidates || candidates.length === 0) return new Set();
    const sets = await lookupBlacklistForEmails(candidates, userId);
    if (!sets || (sets.emails.size === 0 && sets.domains.size === 0)) return new Set();
    const blocked = new Set();
    for (const raw of candidates) {
      if (typeof raw !== 'string') continue;
      const e = raw.trim().toLowerCase();
      if (!e || !e.includes('@')) continue;
      if (sets.emails.has(e)) { blocked.add(e); continue; }
      const dom = e.substring(e.indexOf('@'));
      if (dom && sets.domains.has(dom)) blocked.add(e);
    }
    return blocked;
  } catch (err) {
    console.error('[SPY-PREVIEW] Blacklist precheck failed:', err);
    return new Set();
  }
}

export default function useSpyIntelligence({ userId, handleEdit, handleBulkEdit, displayRows, saveScrollPosition, restoreScrollPosition, fetchData }) {
  // Letzte Sicherung vor dem Function-Call — die Dialoge sperren bereits.
  const webResearchLicensed = useResearchLicenseRef('hasWebResearch');

  // ── State ──
  const [spyLoadingRows, setSpyLoadingRows] = useState({});
  const [spyDataPreview, setSpyDataPreview] = useState({ isOpen: false, intelligenceData: null, contactData: null, rowIndex: null, blacklistedEmails: new Set() });
  const [spySettings, setSpySettings] = useState(DEFAULT_SPY_SETTINGS);

  // Stable refs for values used in callbacks to avoid unnecessary re-creations
  const displayRowsRef = useRef(displayRows);
  displayRowsRef.current = displayRows;
  const spySettingsRef = useRef(spySettings);
  spySettingsRef.current = spySettings;
  const fetchDataRef = useRef(fetchData);
  fetchDataRef.current = fetchData;

  // Blacklist removal queue — populated whenever Web Research applies and
  // discovers emails on the blacklist. Surfaced to the user as a small
  // "X Contact(s) → Blacklist [Remove]" dialog rendered by the table.
  const [blacklistRemovalQueue, setBlacklistRemovalQueue] = useState([]); // [{ id, name, blockedEmails: string[] }]
  const [removingBlacklistContacts, setRemovingBlacklistContacts] = useState(false);

  // Mass Spy state
  const [isMassSpyActive, setIsMassSpyActive] = useState(false);
  const [massSpyQueue, setMassSpyQueue] = useState([]);
  const [massSpyProgress, setMassSpyProgress] = useState(0);
  const [massSpyTotal, setMassSpyTotal] = useState(0);
  const [massSpyResults, setMassSpyResults] = useState(null);
  const [massSpyContacts, setMassSpyContacts] = useState([]);
  const [massSpyProcessedData, setMassSpyProcessedData] = useState([]);
  const [showMassSpyReview, setShowMassSpyReview] = useState(false);

  // ── Load Spy Settings ──
  useEffect(() => {
    if (!userId) return;
    const spySettingsDbRef = ref(database, `World/${userId}/settings/spySettings`);
    const unsubscribe = onValue(spySettingsDbRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setSpySettings(prev => ({
          // 'leadPotential' is retired (replaced by the dedicated Potential
          // Analyser) — never forward it to the crawler, even for legacy configs.
          activeModules: (data.activeModules || prev.activeModules).filter(m => m !== 'leadPotential'),
          advancedSettings: { ...prev.advancedSettings, ...(data.advancedSettings || {}) },
          customContext: data.customContext || ''
        }));
      }
    });
    return () => unsubscribe();
  }, [userId]);

  // ── Simple intelligence update (used by old spy flow) ──
  const updateContactWithIntelligence = useCallback(async (rowId, intelligenceData, contactFallback = null) => {
    const updates = [];

    // Same blacklist guard as the full apply path — covers the legacy
    // selective-apply flow as well. Uses the scalable batch lookup so cost
    // stays bounded by the input set, not the blacklist size.
    const candidateEmails = [
      ...(Array.isArray(intelligenceData.emails) ? intelligenceData.emails : []),
      ...collectCandidateEmails(intelligenceData),
    ];
    const blacklistSets = candidateEmails.length > 0
      ? await lookupBlacklistForEmails(candidateEmails, userId)
      : { emails: new Set(), domains: new Set() };

    if (intelligenceData.emails && intelligenceData.emails.length > 0) {
      const valid = intelligenceData.emails.filter(e => e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
      const allowed = filterBlacklistedEmails(valid, blacklistSets);
      const skipped = valid.length - allowed.length;
      if (skipped > 0) console.log(`[SPY-SELECTIVE] Skipped ${skipped} blacklisted email${skipped > 1 ? 's' : ''} for ${rowId}`);
      if (allowed.length > 0) updates.push({ field: 'lead_email', value: allowed.join(', ') });
    }
    if (intelligenceData.socialProfiles) {
      const { linkedin, instagram, facebook, tiktok } = intelligenceData.socialProfiles;
      if (linkedin && (linkedin.startsWith('http') || linkedin.includes('linkedin.com'))) updates.push({ field: 'lead_linkedin', value: linkedin.startsWith('http') ? linkedin : `https://${linkedin}` });
      if (instagram && (instagram.startsWith('http') || instagram.includes('instagram.com'))) updates.push({ field: 'lead_instagram', value: instagram.startsWith('http') ? instagram : `https://${instagram}` });
      if (facebook && (facebook.startsWith('http') || facebook.includes('facebook.com'))) updates.push({ field: 'lead_facebook', value: facebook.startsWith('http') ? facebook : `https://${facebook}` });
      if (tiktok && (tiktok.startsWith('http') || tiktok.includes('tiktok.com'))) updates.push({ field: 'lead_tiktok', value: tiktok.startsWith('http') ? tiktok : `https://${tiktok}` });
    }
    if (intelligenceData.contactInfo) {
      const { phones, website } = intelligenceData.contactInfo;
      if (phones && Array.isArray(phones) && phones[0]?.trim()) updates.push({ field: 'lead_telephone', value: phones[0] });
      if (website && website.startsWith('http')) updates.push({ field: 'lead_website', value: website });
    }
    const addressContact = displayRowsRef.current.find(row => row.id === rowId) || contactFallback || {};
    const addressPatch = addressUpdatesForContact(intelligenceData, addressContact);
    for (const [field, value] of Object.entries(addressPatch)) {
      updates.push({ field, value });
    }

    const hasSpyDMs = intelligenceData.decisionMakers?.length > 0;
    if (intelligenceData.personInfo && !hasSpyDMs) {
      const { firstName, lastName, position } = intelligenceData.personInfo;
      if (firstName || lastName) {
        const currentData = displayRowsRef.current.find(row => row.id === rowId);
        let existing = [];
        try { if (currentData?.lead_decision_makers) existing = typeof currentData.lead_decision_makers === 'string' ? JSON.parse(currentData.lead_decision_makers) : currentData.lead_decision_makers; } catch (e) {}
        const fullName = [firstName, lastName].filter(Boolean).join(' ');
        if (!existing.some(dm => dm.name?.toLowerCase() === fullName.toLowerCase())) {
          const newDm = { name: fullName };
          if (position) newDm.role = position;
          updates.push({ field: 'lead_decision_makers', value: JSON.stringify([...existing, newDm]) });
        }
      }
    }
    if (intelligenceData.businessInfo) {
      const { description, rating, reviews } = intelligenceData.businessInfo;
      if (description?.trim()) updates.push({ field: 'lead_description', value: description });
      if (rating && rating > 0) updates.push({ field: 'lead_rating', value: rating });
      if (reviews && reviews > 0) updates.push({ field: 'lead_reviews', value: reviews });
    }

    for (const u of updates) {
      try { await handleEdit(rowId, u.field, u.value); } catch (e) { console.error(`[UPDATE-ERROR] ${u.field}:`, e); }
    }
    await handleEdit(rowId, 'is_ready_for_spy', false);
    await handleEdit(rowId, 'spy_last_updated', new Date().toISOString());
    await handleEdit(rowId, 'spy_confidence_score', intelligenceData.confidenceScore || 0);
    return updates.length;
  }, [handleEdit, userId]);

  // ── Spy Preview Apply (full intelligence pipeline) ──
  const handleSpyPreviewClose = useCallback(() => {
    setSpyDataPreview(prev => {
      setSpyLoadingRows(lr => {
        const next = { ...lr };
        if (prev.contactData?.id) delete next[prev.contactData.id];
        return next;
      });
      return { isOpen: false, intelligenceData: null, contactData: null, rowIndex: null, blacklistedEmails: new Set() };
    });
    setTimeout(restoreScrollPosition, 50);
  }, [restoreScrollPosition]);

  const handleSpyPreviewApply = useCallback(async (intelligenceData, contactData = null, rowIndex = null) => {
    const actualContactData = contactData || spyDataPreview.contactData;
    if (!actualContactData || !intelligenceData) return;

    try {
      // Re-check newly-discovered emails (lead_email + decision-maker
      // emails) against the blacklist. We use the scalable batch lookup so
      // a 30k-entry blacklist costs ~N reads (N = unique candidate emails
      // + their domains) instead of pulling the whole collection.
      const candidateEmails = collectCandidateEmails(intelligenceData);
      const blacklistSets = candidateEmails.length > 0
        ? await lookupBlacklistForEmails(candidateEmails, userId)
        : { emails: new Set(), domains: new Set() };
      const { updates, blacklistedEmails } = buildSpyApplyUpdates(intelligenceData, actualContactData, blacklistSets);

      // Queue this contact for one-click removal if any of its discovered
      // emails matched the blacklist. The user gets a confirmation dialog
      // rendered by the table.
      if (blacklistedEmails.length > 0) {
        setBlacklistRemovalQueue(prev => {
          if (prev.some(e => e.id === actualContactData.id)) return prev;
          return [...prev, {
            id: actualContactData.id,
            name: actualContactData.business_name || actualContactData.lead_email || 'Unknown',
            blockedEmails: blacklistedEmails,
          }];
        });
      }

      for (const update of updates) {
        try {
          await handleEdit(actualContactData.id, update.field, update.value);
        } catch (error) {
          console.error(`[UPDATE-ERROR] ${update.field}:`, error);
        }
      }

      await handleEdit(actualContactData.id, 'is_ready_for_spy', false);
      await handleEdit(actualContactData.id, 'spy_last_updated', new Date().toISOString());
      await handleEdit(actualContactData.id, 'spy_confidence_score', intelligenceData.confidenceScore || 0);

      const ri = buildResearchIntelligence(intelligenceData, actualContactData.researchIntelligence);
      try { await handleEdit(actualContactData.id, 'researchIntelligence', ri); } catch (_) {}
      await recordCompanyRecord(userId, actualContactData.id, 'web_research', actualContactData, {
        ...actualContactData,
        researchIntelligence: ri,
      });

      try {
        const freshData = { ...actualContactData };
        for (const u of updates) freshData[u.field] = u.value;
        freshData.researchIntelligence = ri;
        computeAndStoreSignals(userId, actualContactData.id, freshData, { force: true }).catch(() => {});
      } catch (_) {}

      setTimeout(() => fetchDataRef.current(false), 1000);
    } catch (error) {
      console.error('[SPY-APPLY] Error:', error);
      alert(`Fehler beim Anwenden der Intelligence-Daten: ${error.message}`);
    } finally {
      if (!contactData) handleSpyPreviewClose();
    }
  }, [spyDataPreview, handleEdit, userId, handleSpyPreviewClose]);

  const handleSpyPreviewSelectiveApply = useCallback(async (filteredIntelligenceData) => {
    const { contactData, intelligenceData: fullIntelligenceData } = spyDataPreview;
    if (!contactData || !filteredIntelligenceData) return;
    try {
      await updateContactWithIntelligence(contactData.id, filteredIntelligenceData, contactData);

      const ri = buildResearchIntelligence(fullIntelligenceData || filteredIntelligenceData, contactData.researchIntelligence);
      try { await handleEdit(contactData.id, 'researchIntelligence', ri); } catch (_) {}
      await recordCompanyRecord(userId, contactData.id, 'web_research', contactData, {
        ...contactData,
        researchIntelligence: ri,
      });

      try {
        const freshData = { ...contactData };
        if (filteredIntelligenceData.businessInfo?.description) freshData.lead_description = filteredIntelligenceData.businessInfo.description;
        if (filteredIntelligenceData.contactInfo?.website) freshData.lead_website = filteredIntelligenceData.contactInfo.website;
        freshData.researchIntelligence = ri;
        computeAndStoreSignals(userId, contactData.id, freshData, { force: true }).catch(() => {});
      } catch (_) {}
    } catch (error) {
      console.error('[SPY-SELECTIVE] Error:', error);
      alert(`Fehler beim Anwenden der ausgewählten Daten: ${error.message}`);
    } finally {
      handleSpyPreviewClose();
    }
  }, [spyDataPreview, updateContactWithIntelligence, handleEdit, userId, handleSpyPreviewClose]);

  const handleSpyClick = useCallback(async (index) => {
    if (!webResearchLicensed.current) return;
    saveScrollPosition();
    const rowData = displayRowsRef.current[index];
    if (!rowData) { alert("Keine Daten für diesen Eintrag vorhanden."); return; }
    if (!rowData.business_name && !rowData.lead_website) { alert("Mindestens Business-Name oder Website-URL erforderlich für Intelligence-Analyse."); return; }

    const currentSettings = spySettingsRef.current;
    setSpyLoadingRows(prev => ({ ...prev, [rowData.id]: true }));
    try {
      const spyCreditCost = calculateSpyCost(currentSettings.activeModules);
      try {
        const deductCreditsFn = httpsCallable(functions, 'deductCredits');
        await deductCreditsFn({ action: 'web_research', amount: spyCreditCost, worldId: userId, metadata: { contactId: rowData.id, modules: currentSettings.activeModules } });
      } catch (creditError) {
        const msg = creditError?.message || '';
        if (msg.includes('Not enough credits') || creditError?.code === 'resource-exhausted') {
          alert(`Not enough credits for Web Research (${spyCreditCost} credits required). Please add more credits to continue.`);
          setSpyLoadingRows(prev => { const n = { ...prev }; delete n[rowData.id]; return n; });
          return;
        }
        throw creditError;
      }

      const payload = buildIntelligencePayload(rowData, userId, currentSettings);
      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.newWorldWebCrawler(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.status === 429) {
        alert('Daily research limit reached. The limit resets at midnight.');
        return;
      }
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();

      if (result.success && result.intelligenceData) {
        const { confidenceScore } = result.intelligenceData;
        if (confidenceScore && confidenceScore > 50) {
          try {
            await handleSpyPreviewApply(result.intelligenceData, rowData, index);
          } catch (error) {
            // On apply failure we still open the preview — surface blacklist
            // hits visually so the user can see why the apply may have stalled.
            const blocked = await computeBlacklistedEmailsSet(result.intelligenceData, userId);
            setSpyDataPreview({ isOpen: true, intelligenceData: result.intelligenceData, contactData: rowData, rowIndex: index, blacklistedEmails: blocked });
          }
        } else {
          // Compute which discovered emails are on the blacklist BEFORE opening
          // the preview so the modal can render them with a strike-through and
          // "🚫 Blacklist" badge — user sees the conflict before clicking Apply.
          const blocked = await computeBlacklistedEmailsSet(result.intelligenceData, userId);
          setSpyDataPreview({ isOpen: true, intelligenceData: result.intelligenceData, contactData: rowData, rowIndex: index, blacklistedEmails: blocked });
        }
      } else {
        throw new Error("Keine Intelligence-Daten erhalten");
      }
    } catch (error) {
      console.error("[SPY] Error:", error);
      alert(`Fehler bei der Intelligence-Analyse: ${error.message}`);
    } finally {
      setSpyLoadingRows(prev => { const n = { ...prev }; delete n[rowData.id]; return n; });
      setTimeout(restoreScrollPosition, 50);
    }
  }, [userId, saveScrollPosition, restoreScrollPosition, handleSpyPreviewApply, webResearchLicensed]);

  // ── Mass Spy: start (with credit pre-check) ──
  const handleStartMassSpy = useCallback(async (selectedContactIds, selectedCount, creditBalance) => {
    if (!webResearchLicensed.current) return;
    if (selectedCount === 0) return;

    const contactsForIntelligence = displayRowsRef.current
      .filter(row => row?.id && selectedContactIds.includes(row.id))
      .filter(row => (row.business_name?.trim()) || (row.lead_website?.trim()));

    if (contactsForIntelligence.length === 0) {
      alert("None of the selected contacts have a business name or website URL for intelligence analysis.");
      return;
    }

    if (typeof creditBalance === 'number') {
      const costPerSpy = calculateSpyCost(spySettingsRef.current.activeModules);
      const affordable = Math.floor(creditBalance / costPerSpy);
      if (affordable < contactsForIntelligence.length) {
        const proceed = window.confirm(
          `Low credits: You have ${creditBalance} credits (${costPerSpy} per research).\n` +
          `Only ${affordable} of ${contactsForIntelligence.length} contacts can be researched.\n\nContinue anyway?`
        );
        if (!proceed) return;
      }
    }

    setMassSpyQueue(contactsForIntelligence);
    setMassSpyTotal(contactsForIntelligence.length);
    setMassSpyProgress(0);
    setMassSpyProcessedData([]);
    setShowMassSpyReview(false);
    setMassSpyResults({
      processedCount: 0,
      skippedCount: selectedCount - contactsForIntelligence.length,
      emailsFound: 0, descriptionsFound: 0, socialProfilesFound: 0, decisionMakersFound: 0,
      successCount: 0, errorCount: 0, lowConfidenceCount: 0, highConfidenceCount: 0
    });
    setMassSpyContacts(contactsForIntelligence);
    setIsMassSpyActive(true);
  }, [webResearchLicensed]);

  // ── Mass Spy: parallel queue processor ──
  const massSpyRunningRef = useRef(false);
  const massSpyCancelRef = useRef(false);

  useEffect(() => {
    if (!isMassSpyActive || massSpyQueue.length === 0 || massSpyRunningRef.current) return;
    massSpyRunningRef.current = true;
    massSpyCancelRef.current = false;

    const CONCURRENCY = 5;
    const queue = [...massSpyQueue];
    const currentSettings = spySettingsRef.current;
    const spyCreditCost = calculateSpyCost(currentSettings.activeModules);
    let creditExhausted = false;
    let rateLimited = false;

    const processOne = async (contact) => {
      if (creditExhausted || rateLimited || massSpyCancelRef.current) return;

      try {
        const deductCreditsFn = httpsCallable(functions, 'deductCredits');
        await deductCreditsFn({ action: 'web_research', amount: spyCreditCost, worldId: userId, metadata: { contactId: contact.id, modules: currentSettings.activeModules, massSpy: true } });
      } catch (creditError) {
        const msg = creditError?.message || '';
        if (msg.includes('Not enough credits') || creditError?.code === 'resource-exhausted') {
          creditExhausted = true;
          setMassSpyProcessedData(prev => [...prev, {
            originalContact: contact, intelligenceData: null,
            confidenceScore: 0, emailsFound: 0, hasDescription: false,
            socialProfilesFound: 0, status: 'failed', error: 'Not enough credits'
          }]);
          setMassSpyResults(prev => ({ ...prev, errorCount: prev.errorCount + 1 }));
          setMassSpyProgress(prev => prev + 1);
          return;
        }
        throw creditError;
      }

      const payload = buildIntelligencePayload(contact, userId, currentSettings);
      const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.newWorldWebCrawler(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (response.status === 429) {
        rateLimited = true;
        setMassSpyProcessedData(prev => [...prev, {
          originalContact: contact, intelligenceData: null,
          confidenceScore: 0, emailsFound: 0, hasDescription: false,
          socialProfilesFound: 0, status: 'failed', error: 'Daily research limit reached'
        }]);
        setMassSpyResults(prev => ({ ...prev, errorCount: prev.errorCount + 1 }));
        setMassSpyProgress(prev => prev + 1);
        return;
      }
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();

      if (result.success && result.intelligenceData) {
        const { confidenceScore } = result.intelligenceData;
        const emailsFoundCount = [
          ...(result.intelligenceData.emailsFoundInSearch || []),
          ...(result.intelligenceData.aiContentAnalysis?.contactSummary?.emails || [])
        ].filter(email => email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)).length;
        const hasDescription = !!(result.intelligenceData.description || result.intelligenceData.aiContentAnalysis?.detailedSummary);
        const socialProfilesFound = Object.values({
          ...(result.intelligenceData.socialProfiles || {}),
          ...(result.intelligenceData.aiAnalysis?.socialMediaLinks || {}),
          ...(result.intelligenceData.aiContentAnalysis?.socialMediaProfiles || {})
        }).filter(Boolean).length;
        const dmCount = (result.intelligenceData.decisionMakers || []).length;

        setMassSpyProcessedData(prev => [...prev, {
          originalContact: contact, intelligenceData: result.intelligenceData,
          confidenceScore: confidenceScore || 0, emailsFound: emailsFoundCount,
          hasDescription, socialProfilesFound, status: 'success'
        }]);
        setMassSpyResults(prev => ({
          ...prev, successCount: prev.successCount + 1,
          emailsFound: prev.emailsFound + emailsFoundCount,
          descriptionsFound: prev.descriptionsFound + (hasDescription ? 1 : 0),
          socialProfilesFound: prev.socialProfilesFound + socialProfilesFound,
          decisionMakersFound: prev.decisionMakersFound + dmCount,
          highConfidenceCount: prev.highConfidenceCount + (confidenceScore > 50 ? 1 : 0),
          lowConfidenceCount: prev.lowConfidenceCount + (confidenceScore <= 50 ? 1 : 0)
        }));
      } else {
        throw new Error("Keine Intelligence-Daten erhalten");
      }
    };

    const runWorker = async () => {
      while (queue.length > 0 && !creditExhausted && !rateLimited && !massSpyCancelRef.current) {
        const contact = queue.shift();
        try {
          await processOne(contact);
        } catch (error) {
          console.error(`[MASS-SPY] Error for ${contact.business_name || contact.id}:`, error);
          setMassSpyProcessedData(prev => [...prev, {
            originalContact: contact, intelligenceData: null,
            confidenceScore: 0, emailsFound: 0, hasDescription: false,
            socialProfilesFound: 0, status: 'failed', error: error.message
          }]);
          setMassSpyResults(prev => ({ ...prev, errorCount: prev.errorCount + 1 }));
        } finally {
          setMassSpyProgress(prev => prev + 1);
        }
      }
    };

    const runAll = async () => {
      const workers = Array.from({ length: Math.min(CONCURRENCY, queue.length) }, () => runWorker());
      await Promise.all(workers);

      massSpyRunningRef.current = false;

      if (massSpyCancelRef.current) return;

      if (creditExhausted) {
        alert(`Not enough credits — Mass Research stopped. Please add more credits to continue.`);
      }
      if (rateLimited) {
        alert(`Daily research limit reached — Mass Research stopped. The limit resets at midnight.`);
      }

      setMassSpyQueue([]);
      setShowMassSpyReview(true);
      setIsMassSpyActive(false);
    };

    runAll();
  }, [isMassSpyActive, massSpyQueue, userId]);

  const closeMassSpy = useCallback(() => {
    massSpyCancelRef.current = true;
    massSpyRunningRef.current = false;
    setIsMassSpyActive(false);
    setMassSpyQueue([]);
    setShowMassSpyReview(false);
    setMassSpyProcessedData([]);
  }, []);

  const handleMassSpyReviewConfirm = useCallback(async () => {
    const successItems = massSpyProcessedData.filter(pc => pc.status === 'success' && pc.intelligenceData);
    if (successItems.length === 0) { closeMassSpy(); return; }

    console.log(`[MASS-SPY-APPLY] Bulk applying ${successItems.length} contacts...`);
    const BATCH_SIZE = 5;

    // Safety net: a stalled Firestore write/read (network hiccup, no server
    // ack) leaves an `await` pending forever, which would freeze the
    // "Applying…" spinner indefinitely. We race every awaited op against a
    // timeout so the apply ALWAYS finishes and the modal closes. The local
    // data is already updated optimistically (handleBulkEdit → setDbData) and
    // Firestore retries the actual writes in the background, so proceeding
    // without the server ack is safe.
    const withTimeout = (promise, ms, label) => Promise.race([
      Promise.resolve(promise).catch((e) => { console.error(`[MASS-SPY-APPLY] ${label} failed:`, e); return undefined; }),
      new Promise((resolve) => setTimeout(() => {
        console.warn(`[MASS-SPY-APPLY] ${label} timed out after ${ms}ms — continuing in background`);
        resolve(undefined);
      }, ms)),
    ]);

    try {
      // Build ONE list of every candidate email discovered across the whole
      // batch (lead emails + DM emails on every contact), then run a single
      // scalable lookup. Cost = O(unique emails + domains) instead of
      // O(blacklist size). Same `{ emails, domains }` shape is reused for
      // every contact below — no extra Firestore reads per contact.
      const allCandidateEmails = collectCandidateEmails(
        successItems.map(pc => pc.intelligenceData)
      );
      const blacklistSets = (allCandidateEmails.length > 0
        ? await withTimeout(lookupBlacklistForEmails(allCandidateEmails, userId), 8000, 'Blacklist lookup')
        : null) || { emails: new Set(), domains: new Set() };
      console.log(
        `[MASS-SPY-APPLY] Blacklist lookup: ${allCandidateEmails.length} candidate emails → ` +
        `${blacklistSets.emails.size} email hits, ${blacklistSets.domains.size} domain hits`
      );
      const blacklistHits = []; // [{ id, name, blockedEmails }]

      for (let i = 0; i < successItems.length; i += BATCH_SIZE) {
        const batch = successItems.slice(i, i + BATCH_SIZE);
        await Promise.all(batch.map(async (pc) => {
          try {
            const { updates, blacklistedEmails } = buildSpyApplyUpdates(pc.intelligenceData, pc.originalContact, blacklistSets);
            if (blacklistedEmails.length > 0) {
              blacklistHits.push({
                id: pc.originalContact.id,
                name: pc.originalContact.business_name || pc.originalContact.lead_email || 'Unknown',
                blockedEmails: blacklistedEmails,
              });
            }
            const allFields = {};
            for (const u of updates) allFields[u.field] = u.value;
            allFields.is_ready_for_spy = false;
            allFields.spy_last_updated = new Date().toISOString();
            allFields.spy_confidence_score = pc.intelligenceData.confidenceScore || 0;

            const ri = buildResearchIntelligence(pc.intelligenceData, pc.originalContact.researchIntelligence);
            allFields.researchIntelligence = ri;

            await withTimeout(handleBulkEdit(pc.originalContact.id, allFields), 12000, `Write ${pc.originalContact.id}`);
            if (blacklistedEmails.length === 0) {
              await withTimeout(recordCompanyRecord(userId, pc.originalContact.id, 'web_research', pc.originalContact, {
                ...pc.originalContact,
                researchIntelligence: ri,
              }), 8000, `Record ${pc.originalContact.id}`);
            }

            computeAndStoreSignals(userId, pc.originalContact.id, { ...pc.originalContact, ...allFields }, { force: true }).catch(() => {});
          } catch (e) {
            console.error(`[MASS-SPY-APPLY] Failed for ${pc.originalContact.business_name || pc.originalContact.id}:`, e);
          }
        }));
      }

      console.log('[MASS-SPY-APPLY] Bulk apply complete, reloading data...');
      // Auto-remove blacklisted contacts in the SAME step — no extra modal.
      // Mass Spy intentionally batches this together with the apply, so the
      // user doesn't get a second confirmation popup. Single Web Research
      // (handleSpyPreviewApply) still goes through the explicit modal.
      if (blacklistHits.length > 0) {
        let removed = 0;
        for (const hit of blacklistHits) {
          try {
            await withTimeout(deleteDoc(doc(firestore, `World/${userId}/db/${hit.id}`)), 8000, `Delete ${hit.id}`);
            removed++;
          } catch (err) {
            console.error('[MASS-SPY-APPLY] Failed to remove blacklisted contact', hit.id, err);
          }
        }
        console.log(`[MASS-SPY-APPLY] Auto-removed ${removed}/${blacklistHits.length} blacklisted contacts`);
      }
    } catch (e) {
      console.error('[MASS-SPY-APPLY] Unexpected error during apply:', e);
    } finally {
      // ALWAYS reload + close, even if something above stalled or threw — the
      // user must never be stuck on an endless "Applying…" spinner.
      setTimeout(() => { fetchDataRef.current(false); }, 500);
      closeMassSpy();
    }
  }, [massSpyProcessedData, handleBulkEdit, userId, closeMassSpy]);

  const handleMassSpyReviewCancel = useCallback(() => { closeMassSpy(); }, [closeMassSpy]);

  // Confirm removal — deletes every queued contact doc, clears the queue.
  const confirmBlacklistRemoval = useCallback(async () => {
    if (blacklistRemovalQueue.length === 0 || removingBlacklistContacts) return;
    setRemovingBlacklistContacts(true);
    let removed = 0;
    for (const entry of blacklistRemovalQueue) {
      try {
        await deleteDoc(doc(firestore, `World/${userId}/db/${entry.id}`));
        removed++;
      } catch (err) {
        console.error('[BL-REMOVE] Failed to delete', entry.id, err);
      }
    }
    setBlacklistRemovalQueue([]);
    setRemovingBlacklistContacts(false);
    setTimeout(() => { fetchDataRef.current(false); }, 300);
    console.log(`[BL-REMOVE] Removed ${removed}/${blacklistRemovalQueue.length} blacklist contacts`);
  }, [blacklistRemovalQueue, removingBlacklistContacts, userId]);

  const dismissBlacklistRemoval = useCallback(() => {
    setBlacklistRemovalQueue([]);
  }, []);

  return {
    spyLoadingRows,
    spyDataPreview,
    spySettings,
    handleSpyClick,
    handleSpyPreviewClose,
    handleSpyPreviewApply,
    handleSpyPreviewSelectiveApply,
    updateContactWithIntelligence,
    // Mass spy
    isMassSpyActive,
    massSpyQueue,
    massSpyProgress,
    massSpyTotal,
    massSpyResults,
    massSpyContacts,
    massSpyProcessedData,
    showMassSpyReview,
    handleStartMassSpy,
    closeMassSpy,
    handleMassSpyReviewConfirm,
    handleMassSpyReviewCancel,
    // Blacklist removal queue (Web Research + Mass Spy)
    blacklistRemovalQueue,
    removingBlacklistContacts,
    confirmBlacklistRemoval,
    dismissBlacklistRemoval,
  };
}
